import React, { useState } from "react";

// The group control: pick a list, make a new one, rename or delete the current
// one. Deliberately not nav tabs — lists are content the user creates, and the
// nav shouldn't grow an entry every time they make one.
export default function ListPicker({
  lists,
  activeId,
  onSelect,
  onCreate,
  onRename,
  onDelete,
  onImport,          // (name, cards, { intoPinned }) => void
  game,
  /* An optional first chip that behaves like a list for picking, importing and
     exporting, but isn't one: the collection. It can't be renamed or deleted,
     because "delete list" reaching what you own would be the worst button in
     the app. Shape: { id, name, cards, game }. */
  pinned,
  /* Further fixed chips that are not lists at all — Goals lives here. They
     can be picked and nothing else: no import, export, rename or delete,
     because none of those mean anything for a screen that is not a list of
     cards. Shape: { id, name, count, title }. */
  fixed = [],
}) {
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [importError, setImportError] = useState("");
  const fileRef = React.useRef(null);

  const isPinned = !!pinned && activeId === pinned.id;
  const isFixed = fixed.some((f) => f.id === activeId);
  const active = isPinned ? pinned : lists.find((l) => l.id === activeId);

  /* Lists live in this browser and nowhere else — there are no accounts, so
     nothing syncs. A file is how a list reaches your phone, or survives
     clearing site data. It also replaced shipping a personal list in the
     bundle, which is what everyone else used to see. */
  const exportActive = () => {
    if (!active) return;
    const payload = {
      pokepricer: 1,
      name: active.name,
      game: active.game,
      exportedAt: new Date().toISOString(),
      cards: active.cards,
    };
    const url = URL.createObjectURL(
      new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" })
    );
    const a = document.createElement("a");
    a.href = url;
    a.download = `${active.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const importFile = async (file) => {
    setImportError("");
    if (!file) return;
    try {
      const parsed = JSON.parse(await file.text());
      // Accept either our export shape or a bare array of cards, so a file
      // someone hand-edited still loads.
      const cards = Array.isArray(parsed) ? parsed : parsed?.cards;
      if (!Array.isArray(cards) || cards.length === 0) {
        setImportError("That file has no cards in it.");
        return;
      }
      const name = (!Array.isArray(parsed) && parsed?.name) || file.name.replace(/\.json$/i, "");
      // Where it lands follows the chip you were on: the collection merges,
      // a list is created. The parent decides; this only reports which.
      onImport?.(name, cards, { intoPinned: isPinned });
    } catch {
      setImportError("Couldn't read that file — it needs to be a list exported from here.");
    }
  };

  const submitNew = () => {
    if (!draft.trim()) return;
    onCreate(draft);
    setDraft("");
    setCreating(false);
  };

  const submitRename = () => {
    if (!draft.trim()) return;
    onRename(activeId, draft);
    setDraft("");
    setRenaming(false);
  };

  return (
    <div className="lists-bar">
      <div className="lists-chips">
        {pinned && (
          <button
            className={
              "wl-chip wl-chip--fixed" + (isPinned ? " wl-chip--active" : "")
            }
            aria-pressed={isPinned}
            onClick={() => onSelect(pinned.id)}
            title="What you own — this one can't be deleted"
          >
            {pinned.name}
            <span className="lists-count">{pinned.cards.length}</span>
          </button>
        )}
        {fixed.map((f) => (
          <button
            key={f.id}
            className={"wl-chip wl-chip--fixed" + (activeId === f.id ? " wl-chip--active" : "")}
            aria-pressed={activeId === f.id}
            onClick={() => onSelect(f.id)}
            title={f.title}
          >
            {f.name}
            {f.count != null && <span className="lists-count">{f.count}</span>}
          </button>
        ))}
        {lists.map((l) => (
          <button
            key={l.id}
            className={l.id === activeId ? "wl-chip wl-chip--active" : "wl-chip"}
            aria-pressed={l.id === activeId}
            onClick={() => onSelect(l.id)}
          >
            {l.name}
            <span className="lists-count">{l.cards.length}</span>
          </button>
        ))}
        <button className="wl-chip lists-new" onClick={() => { setCreating(true); setRenaming(false); setDraft(""); }}>
          + New list
        </button>
      </div>

      {(creating || renaming) && (
        <div className="lists-form">
          <input
            autoFocus
            className="lists-input"
            value={draft}
            placeholder={renaming ? "Rename this list" : "Name your list"}
            aria-label={renaming ? "New name for this list" : "Name for the new list"}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") renaming ? submitRename() : submitNew();
              if (e.key === "Escape") { setCreating(false); setRenaming(false); setDraft(""); }
            }}
          />
          <button className="btn btn-primary btn-sm" onClick={renaming ? submitRename : submitNew}>
            {renaming ? "Rename" : "Create"}
          </button>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => { setCreating(false); setRenaming(false); setDraft(""); }}
          >
            Cancel
          </button>
        </div>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="application/json,.json"
        style={{ display: "none" }}
        aria-label="Import a list from a file"
        onChange={(e) => {
          importFile(e.target.files?.[0]);
          e.target.value = "";   // so the same file can be picked twice
        }}
      />

      {importError && <p className="msg-error">{importError}</p>}

      {/* Nothing at all for a fixed chip that is not a list: importing into
          Goals is not a thing, and a row of actions that do not apply is a
          row of questions. */}
      {!creating && !renaming && !isFixed && (
        <div className="lists-actions">
          {/* Rename and delete are for lists you made. The collection is not
              one, so those two are simply absent rather than shown disabled —
              a greyed Delete list still invites the click. */}
          {active && !isPinned && (
            <button
              className="lists-link"
              onClick={() => { setRenaming(true); setDraft(active.name); }}
            >
              Rename
            </button>
          )}
          <button className="lists-link" onClick={() => fileRef.current?.click()}>
            Import
          </button>
          {active && active.cards.length > 0 && (
            <button className="lists-link" onClick={exportActive}>
              Export
            </button>
          )}
          {active && !isPinned && (
            <button
              className="lists-link lists-link--danger"
              onClick={() => {
                const count = active.cards.length;
                const warning = count
                  ? `Delete "${active.name}" and the ${count} card${count === 1 ? "" : "s"} on it?`
                  : `Delete "${active.name}"?`;
                // eslint-disable-next-line no-alert
                if (window.confirm(warning)) onDelete(active.id);
              }}
            >
              Delete list
            </button>
          )}
        </div>
      )}
    </div>
  );
}
